// idTokenRefresher.jsx
import { jwtDecode } from 'jwt-decode';
import { getAuth } from 'firebase/auth';

class IdTokenRefresher {
  constructor() {
    this.refreshTimer = null;
    this.tokenKey = 'emsUser'; // Key in localStorage
    this.refreshBuffer = 10 * 60 * 1000; // 10 minutes
    this.auth = getAuth(); // Get Firebase Auth instance
  }

  // Start monitoring the token
  startMonitoring() {
    this.checkAndScheduleRefresh();

    window.addEventListener('storage', (event) => {
      if (event.key === this.tokenKey && event.newValue) {
        this.checkAndScheduleRefresh();
      }
    });

    setInterval(() => this.checkAndScheduleRefresh(), 60000); // every minute

  }

  // Check and schedule token refresh
  checkAndScheduleRefresh() {
    const rawUser = localStorage.getItem(this.tokenKey);

    if (!rawUser) {
      this.clearRefreshTimer();
      return;
    }

    let user;
    try {
      user = JSON.parse(rawUser);
    } catch (error) {
      console.error('[Parse Error] Failed to parse emsUser:', error);
      return;
    }

    // Get the ID token from wherever it's stored in your user object structure
    const idToken = user?.stsTokenManager?.accessToken || 
                    user?.user?.stsTokenManager?.accessToken;

    if (!idToken) {
      this.clearRefreshTimer();
      return;
    }

    try {
      const decodedToken = jwtDecode(idToken);
      const expiryTime = decodedToken.exp * 1000; // convert to ms
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;

      // console.log('[Token Info]', {
      //   expiryTime: new Date(expiryTime).toISOString(),
      //   currentTime: new Date(currentTime).toISOString(),
      //   timeUntilExpiry: `${Math.floor(timeUntilExpiry / 60000)} mins`,
      // });

      const refreshTime = Math.max(timeUntilExpiry - this.refreshBuffer, 0);

      this.clearRefreshTimer();

      if (refreshTime < 5000) {
        this.refreshToken();
      } else {
        this.refreshTimer = setTimeout(() => {
          this.refreshToken();
        }, refreshTime);

      }
    } catch (error) {
      console.error('[Decode Error] Failed to decode idToken:', error);
    }
  }

  // Clear any scheduled refresh
  clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Refresh the token using Firebase's built-in method
  async refreshToken() {
    try {
      console.log('[Refresh] Refreshing ID token...');
      
      // Check if a user is signed in
      const currentUser = this.auth.currentUser;
      
      if (!currentUser) {
        console.error('[Refresh] No authenticated user found');
        return;
      }

      // Force refresh the token
      const newIdToken = await currentUser.getIdToken(true);
      
      // Update the token in localStorage
      const rawUser = localStorage.getItem(this.tokenKey);
      if (!rawUser) {
        console.error('[Update] No emsUser to update.');
        return;
      }

      const user = JSON.parse(rawUser);
      
      // Update the token in the appropriate location based on your structure
      if (user.user && user.user._tokenResponse) {
        user.user._tokenResponse.idToken = newIdToken;
      }
      
      if (user.user && user.user.stsTokenManager) {
        user.user.stsTokenManager.accessToken = newIdToken;
        // Firebase handles the refreshToken internally
      }

      localStorage.setItem(this.tokenKey, JSON.stringify(user));

      console.log('[Refresh] ID token refreshed successfully');

      // Schedule the next refresh
      this.checkAndScheduleRefresh();
    } catch (error) {
      console.error('[Refresh Error] Failed to refresh token:', error);
    }
  }

  // Stop monitoring
  stopMonitoring() {
    this.clearRefreshTimer();
    console.log('[Stop] ID token monitoring stopped');
  }
}

const tokenRefresher = new IdTokenRefresher();

export default tokenRefresher;